// NSARA Stores V2 — création/édition d'un produit en 3 étapes (section 11).
//
// Règle absolue de la mission: l'IMAGE RESTE FACULTATIVE. Cloudinary étant
// actuellement en échec sur le staging, un téléversement raté affiche un
// message propre et n'empêche JAMAIS d'enregistrer le produit (le produit est
// créé avec photo_url null, exactement comme le backend le permet déjà).
//
// Stock: à la CRÉATION le stock initial est saisi ici (POST /api/products crée
// le stock_movement INITIAL correspondant). En ÉDITION le stock n'est pas
// modifiable ici — le backend l'interdit volontairement (PATCH /api/products
// n'écrit jamais stock_quantity) pour garantir qu'aucune variation de stock ne
// se produise sans mouvement tracé. Le seuil d'alerte, lui, est bien supporté
// en création comme en édition: c'est donc lui qui est proposé, plutôt qu'un
// faux interrupteur "suivre le stock" sans effet réel.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, ImagePlus, X, Check, Package } from 'lucide-react';
import { createProduct, updateProduct, uploadImage } from '../../services/apiClient';
import Modal from './Modal';

const STEPS = [
  { id: 1, label: 'Produit' },
  { id: 2, label: 'Prix & stock' },
  { id: 3, label: 'Publication' }
];

const emptyForm = {
  name: '', description: '', category: '',
  sellingPrice: '', stockQuantity: '', lowStockThreshold: '',
  photoUrl: '', galleryImages: [], detailedDescription: '', compareAtPrice: '',
  deliveryInfo: '', pickupInfo: '', publicEnabled: true
};

const asText = (value) => (value === undefined || value === null ? '' : String(value));

// `product` est une ligne BRUTE de GET /api/products (colonnes de la table, donc
// snake_case). Le prix était lu en `sellingPrice`, une clé qui n'existe pas sur
// ces lignes: le champ « Prix de vente » arrivait donc VIDE à l'édition et
// l'étape 2 refusait l'enregistrement. On lit la vraie colonne, avec une
// tolérance camelCase pour tout appelant qui passerait un objet déjà normalisé.

const fromProduct = (product) => ({
  name: product?.name || '',
  description: product?.description || '',
  category: product?.category || '',
  sellingPrice: asText(product?.selling_price ?? product?.sellingPrice),
  stockQuantity: product?.stock_quantity !== undefined && product?.stock_quantity !== null ? String(product.stock_quantity) : '',
  lowStockThreshold: product?.low_stock_threshold !== undefined && product?.low_stock_threshold !== null ? String(product.low_stock_threshold) : '',
  photoUrl: product?.photo_url || '',
  galleryImages: Array.isArray(product?.gallery_images) ? product.gallery_images : [],
  detailedDescription: product?.detailed_description || '',
  compareAtPrice: product?.compare_at_price !== undefined && product?.compare_at_price !== null ? String(product.compare_at_price) : '',
  deliveryInfo: product?.delivery_info || '',
  pickupInfo: product?.pickup_info || '',
  publicEnabled: product?.public_enabled !== false
});

export default function ProductFormDialog({ open, onClose, onSaved, businessId, shopId, product }) {
  const isEdit = Boolean(product?.id);
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Stabilité de la saisie (P-PRODUCT-V2.1).
  //
  // 1) Le parent re-rend régulièrement avec une NOUVELLE référence d'objet
  //    `product` (liste rechargée, changement d'onglet…). Reconstruire le
  //    formulaire sur la simple identité de cet objet effaçait la saisie en
  //    cours. On ne réinitialise donc que sur un vrai changement de fiche:
  //    ouverture/fermeture du dialogue (`open`) ou autre produit (`product.id`).
  // 2) `onClose` est lui aussi une fonction inline côté parent. On le lit via
  //    une ref pour que `handleClose` ne change pas d'identité à chaque frappe
  //    (une référence instable relançait l'effet de focus de la modale, qui
  //    renvoyait le caret sur le premier champ).
  const productId = product?.id ?? null;
  const productRef = useRef(product);
  const closeRef = useRef(onClose);
  useEffect(() => { productRef.current = product; }, [product]);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const source = productRef.current;
    setForm(source ? fromProduct(source) : emptyForm);
    setStep(1);
    setSaving(false);
    setUploading(false);
    setImageError('');
    setError('');
    setFieldErrors({});
  }, [open, productId]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const validateStep = (target) => {
    const next = {};
    if (target >= 1 && !form.name.trim()) next.name = 'Le nom du produit est requis.';
    if (target >= 2) {
      const price = Number(form.sellingPrice);
      if (form.sellingPrice === '' || !Number.isFinite(price)) next.sellingPrice = 'Indiquez un prix de vente.';
      else if (price < 0) next.sellingPrice = 'Le prix ne peut pas être négatif.';
      if (!isEdit) {
        const stock = Number(form.stockQuantity);
        if (form.stockQuantity === '' || !Number.isFinite(stock)) next.stockQuantity = 'Indiquez le stock disponible.';
        else if (!Number.isInteger(stock) || stock < 0) next.stockQuantity = 'Le stock doit être un nombre entier positif.';
      }
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const goNext = () => { if (validateStep(step)) { setError(''); setStep((s) => Math.min(3, s + 1)); } };
  const goBack = () => { setError(''); setStep((s) => Math.max(1, s - 1)); };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) {
      setImageError('Veuillez sélectionner une image valide.');
      return;
    }
    setImageError('');
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setForm((current) => ({ ...current, photoUrl: url || '' }));
    } catch (err) {
      // Échec d'upload = avertissement, jamais un blocage (règle Cloudinary).
      setImageError(err?.status === 503
        ? 'Téléversement indisponible pour le moment. Vous pouvez enregistrer le produit sans image.'
        : 'Téléversement impossible. Vous pouvez enregistrer le produit sans image.');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryFiles = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, Math.max(0, 5 - form.galleryImages.length));
    event.target.value = '';
    if (!files.length) return;
    if (files.some((file) => !String(file.type || '').startsWith('image/'))) { setImageError('Veuillez sélectionner des images valides.'); return; }
    setImageError(''); setUploading(true);
    try {
      const uploaded = await Promise.all(files.map(async (file) => (await uploadImage(file)).url));
      setForm((current) => ({ ...current, galleryImages: [...current.galleryImages, ...uploaded.filter(Boolean)].slice(0, 5) }));
    } catch (err) {
      setImageError('Certaines images n’ont pas pu être téléversées. Vous pouvez enregistrer le produit sans elles.');
    } finally { setUploading(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!validateStep(2)) { setStep(2); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        detailedDescription: form.detailedDescription.trim(),
        category: form.category.trim(),
        sellingPrice: Number(form.sellingPrice),
        compareAtPrice: form.compareAtPrice === '' ? null : Number(form.compareAtPrice),
        lowStockThreshold: form.lowStockThreshold === '' ? 0 : Number(form.lowStockThreshold),
        photoUrl: form.photoUrl,
        galleryImages: form.galleryImages,
        deliveryInfo: form.deliveryInfo.trim(),
        pickupInfo: form.pickupInfo.trim(),
        publicEnabled: form.publicEnabled
      };
      const saved = isEdit
        ? (await updateProduct(product.id, payload)).product
        : (await createProduct({
          ...payload,
          businessId,
          shopId,
          stockQuantity: Number(form.stockQuantity)
        })).product;
      onSaved?.(saved);
    } catch (err) {
      setError(err?.message || 'Enregistrement impossible. Réessayez.');
      setSaving(false);
    }
  };

  const handleClose = useCallback(() => { if (!saving) closeRef.current?.(); }, [saving]);
  const busy = saving || uploading;

  return (
    <Modal open={open} onClose={handleClose} labelledBy="product-form-title" size="md">
      <form className="stores-dialog" onSubmit={submit} noValidate>
        <div className="stores-dialog-head">
          <span className="stores-dialog-icon" aria-hidden="true"><Package size={18} /></span>
          <div>
            <h2 id="product-form-title" className="stores-dialog-title">{isEdit ? 'Modifier le produit' : 'Ajouter un produit'}</h2>
            <p className="stores-dialog-subtitle">Trois étapes, l’image est facultative.</p>
          </div>
        </div>

        <div className="stores-steps" aria-hidden="true">
          {STEPS.map((item, index) => (
            <React.Fragment key={item.id}>
              <span className={step === item.id ? 'stores-step is-active' : step > item.id ? 'stores-step is-done' : 'stores-step'}>
                <span className="stores-step-dot">{step > item.id ? <Check size={11} /> : item.id}</span>
                {item.label}
              </span>
              {index < STEPS.length - 1 && <span className="stores-step-line" />}
            </React.Fragment>
          ))}
        </div>

        <p className="stores-hint" aria-live="polite">Étape {step} sur 3 — {STEPS[step - 1].label}</p>

        {step === 1 && (
          <>
            <div className="stores-field">
              <label className="stores-label" htmlFor="product-name">Nom *</label>
              <input id="product-name" className="stores-input" value={form.name} onChange={set('name')} maxLength={200} autoComplete="off" placeholder="Ex. Chemise Premium" aria-required="true" aria-invalid={Boolean(fieldErrors.name)} aria-describedby={fieldErrors.name ? 'product-name-error' : undefined} disabled={busy} />
              {fieldErrors.name && <p className="stores-field-error" id="product-name-error" role="alert">{fieldErrors.name}</p>}
            </div>
            <div className="stores-field">
              <label className="stores-label" htmlFor="product-description">Description</label>
              <textarea id="product-description" className="stores-input stores-textarea" value={form.description} onChange={set('description')} maxLength={2000} placeholder="Matière, coupe, tailles disponibles…" disabled={busy} />
            </div>
            <div className="stores-field">
              <label className="stores-label" htmlFor="product-category">Catégorie (facultatif)</label>
              <input id="product-category" className="stores-input" value={form.category} onChange={set('category')} maxLength={120} autoComplete="off" placeholder="Ex. Chemises" disabled={busy} />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="stores-field-row">
              <div className="stores-field">
                <label className="stores-label" htmlFor="product-price">Prix de vente * (F CFA)</label>
                <input id="product-price" className="stores-input" type="number" min="0" step="1" inputMode="numeric" value={form.sellingPrice} onChange={set('sellingPrice')} placeholder="15000" aria-required="true" aria-invalid={Boolean(fieldErrors.sellingPrice)} aria-describedby={fieldErrors.sellingPrice ? 'product-price-error' : undefined} disabled={busy} />
                {fieldErrors.sellingPrice && <p className="stores-field-error" id="product-price-error" role="alert">{fieldErrors.sellingPrice}</p>}
              </div>
              {!isEdit && (
                <div className="stores-field">
                  <label className="stores-label" htmlFor="product-stock">Stock disponible *</label>
                  <input id="product-stock" className="stores-input" type="number" min="0" step="1" inputMode="numeric" value={form.stockQuantity} onChange={set('stockQuantity')} placeholder="10" aria-required="true" aria-invalid={Boolean(fieldErrors.stockQuantity)} aria-describedby={fieldErrors.stockQuantity ? 'product-stock-error' : undefined} disabled={busy} />
                  {fieldErrors.stockQuantity && <p className="stores-field-error" id="product-stock-error" role="alert">{fieldErrors.stockQuantity}</p>}
                </div>
              )}
            </div>
            <div className="stores-field">
              <label className="stores-label" htmlFor="product-threshold">Alerte si le stock passe sous (facultatif)</label>
              <input id="product-threshold" className="stores-input" type="number" min="0" step="1" inputMode="numeric" value={form.lowStockThreshold} onChange={set('lowStockThreshold')} placeholder="2" disabled={busy} />
              <label className="stores-label" htmlFor="product-compare-price" style={{ marginTop: 16 }}>Ancien prix (facultatif, F CFA)</label>
              <input id="product-compare-price" className="stores-input" type="number" min="0" step="1" inputMode="numeric" value={form.compareAtPrice} onChange={set('compareAtPrice')} placeholder="20000" disabled={busy} />
              <p className="stores-hint">
                {isEdit
                  ? 'Le stock se corrige depuis le module Inventaire, pour que chaque variation reste tracée.'
                  : 'Sert à signaler un stock faible dans l’aperçu de la boutique.'}
              </p>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="stores-field">
              <span className="stores-label" id="product-photo-label">Photo (facultatif)</span>
              <div className="stores-logo-box" role="group" aria-labelledby="product-photo-label">
                {form.photoUrl && (
                  <span className="stores-logo-preview">
                    <img src={form.photoUrl} alt="" />
                  </span>
                )}
                <label className="stores-btn stores-btn--outline stores-btn--sm" style={{ cursor: busy ? 'not-allowed' : 'pointer' }}>
                  {uploading ? <Loader2 size={15} aria-hidden="true" className="stores-spin" /> : <ImagePlus size={15} aria-hidden="true" />}
                  {uploading ? 'Téléversement…' : form.photoUrl ? 'Remplacer' : 'Choisir une image'}
                  <input type="file" accept="image/*" onChange={handleFile} disabled={busy} style={{ display: 'none' }} />
                </label>
                {form.photoUrl && (
                  <button type="button" className="stores-icon-btn" aria-label="Retirer la photo" disabled={busy} onClick={() => setForm((c) => ({ ...c, photoUrl: '' }))}>
                    <X size={15} aria-hidden="true" />
                  </button>
                )}
              </div>
              {imageError && <p className="stores-field-error" role="status">{imageError}</p>}
              {!imageError && <p className="stores-hint">Sans photo, le produit est publié quand même avec un visuel de remplacement.</p>}
            </div>

            <div className="stores-field">
              <span className="stores-label" id="product-gallery-label">Photos supplémentaires (facultatif)</span>
              <div className="stores-logo-box" role="group" aria-labelledby="product-gallery-label">
                {form.galleryImages.map((url, index) => <span className="stores-logo-preview" key={url}><img src={url} alt="" /><button type="button" className="stores-icon-btn" aria-label={`Retirer la photo ${index + 2}`} disabled={busy} onClick={() => setForm((current) => ({ ...current, galleryImages: current.galleryImages.filter((image) => image !== url) }))}><X size={13} aria-hidden="true" /></button></span>)}
                {form.galleryImages.length < 5 && <label className="stores-btn stores-btn--outline stores-btn--sm" style={{ cursor: busy ? 'not-allowed' : 'pointer' }}><ImagePlus size={15} aria-hidden="true" /> Ajouter ({form.galleryImages.length}/5)<input type="file" accept="image/*" multiple onChange={handleGalleryFiles} disabled={busy} style={{ display: 'none' }} /></label>}
              </div>
              <p className="stores-hint">Avec la photo principale : six images maximum.</p>
            </div>
            <div className="stores-field"><label className="stores-label" htmlFor="product-detailed-description">Description détaillée (facultatif)</label><textarea id="product-detailed-description" className="stores-input stores-textarea" value={form.detailedDescription} onChange={set('detailedDescription')} maxLength={12000} placeholder="Ajoutez les détails utiles en paragraphes." disabled={busy} /></div>
            <div className="stores-field-row">
              <div className="stores-field"><label className="stores-label" htmlFor="product-delivery-info">Livraison (facultatif)</label><input id="product-delivery-info" className="stores-input" value={form.deliveryInfo} onChange={set('deliveryInfo')} maxLength={1000} placeholder="Ex. Livraison disponible à Bamako" disabled={busy} /></div>
              <div className="stores-field"><label className="stores-label" htmlFor="product-pickup-info">Retrait (facultatif)</label><input id="product-pickup-info" className="stores-input" value={form.pickupInfo} onChange={set('pickupInfo')} maxLength={1000} placeholder="Ex. Retrait possible en boutique" disabled={busy} /></div>
            </div>

            <label className="stores-switch" htmlFor="product-public">
              <input id="product-public" type="checkbox" checked={form.publicEnabled} disabled={busy} onChange={(event) => setForm((c) => ({ ...c, publicEnabled: event.target.checked }))} />
              <span>Publier sur ma vitrine</span>
            </label>
            <p className="stores-hint">Un produit masqué reste gérable ici, mais n’apparaît plus sur la vitrine publique.</p>
          </>
        )}

        {error && <p className="stores-alert stores-alert--error" role="alert">{error}</p>}

        <div className="stores-dialog-actions">
          {step > 1 && <button type="button" className="stores-btn stores-btn--ghost" onClick={goBack} disabled={busy}>Retour</button>}
          {step < 3 && <button type="button" className="stores-btn stores-btn--primary" onClick={goNext} disabled={busy}>Continuer</button>}
          {step === 3 && (
            <button type="submit" className="stores-btn stores-btn--primary" disabled={busy}>
              {saving && <Loader2 size={15} aria-hidden="true" className="stores-spin" />}
              {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Publier le produit'}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
